import React from 'react';
import { motion } from 'framer-motion';

/*
 * NikeNavIcons — bold-minimalist, monochrome, animated main-sidebar icon set.
 *
 * Design system (synthesized from awwwards-2026 / pqoqubbw·icons / Motion research):
 *  - viewBox 0 0 24 24, single 2px stroke, stroke=currentColor, fill=none.
 *  - SQUARE terminals: strokeLinecap="square" + strokeLinejoin="miter" — the
 *    one decision that turns a generic Feather/Lucide set into the editorial
 *    Nike register. Uniform across ALL glyphs (never mix square + round).
 *  - Color is themed entirely via Tailwind text-* on the parent (currentColor),
 *    so the whole set recolors with zero icon edits; spend the color budget on
 *    the active state only.
 *
 * Motion model — variant PROPAGATION (no refs/useAnimation):
 *  The parent <IconNavButton> is a motion.button driving labels
 *  rest | hover | tap | active. This icon's <motion.span> (scale bump) and the
 *  per-glyph signature sub-elements carry matching `variants` and simply follow
 *  the propagated label. Transform/opacity/strokeWidth only (GPU-cheap, no
 *  layout). Hover is on the button (static hit-target), never on a pathLength:0
 *  path — so there's no doom-flicker. prefers-reduced-motion is honoured app-
 *  wide by <MotionConfig reducedMotion="user"> (App.js).
 */

const EASE = [0.4, 0, 0.2, 1];        // Material standard — every hover/state move
const EXPO = [0.22, 1, 0.36, 1];      // expo-out — draw-on / snap signatures only

// transform-origin fixes (SVG default origin is bbox top-left → "flies off canvas")
const SP = { transformOrigin: 'center', transformBox: 'fill-box' };
const HINGE = { transformOrigin: 'left center', transformBox: 'fill-box' };
const BOTTOM = { transformOrigin: 'bottom', transformBox: 'fill-box' };

// Wrapper scale bump — snappy in, relaxed out (the premium asymmetry).
const WRAP = {
  rest: { scale: 1, transition: { duration: 0.28, ease: EASE } },
  hover: { scale: 1.06, transition: { duration: 0.14, ease: EASE } },
  tap: { scale: 0.97, transition: { duration: 0.1, ease: EASE } },
  active: { scale: 1, transition: { duration: 0.2, ease: EASE } },
};

// Each entry returns the inner SVG markup; motion.* only where it animates.
const ICONS = {
  home: () => (
    <>
      <motion.path d="M3 11 L12 3 L21 11"
        variants={{ rest: { y: 0 }, hover: { y: [0, 0.5, 0] } }}
        transition={{ duration: 0.18, ease: EASE }} style={SP} />
      <path d="M5 9.5 L5 21 L19 21 L19 9.5" />
      <path d="M10 21 L10 14 L14 14 L14 21" />
    </>
  ),

  // Toolbox — geometric + bold (reads "tools" faster than a curvy wrench at 20px).
  tools: () => (
    <>
      <rect x="3" y="9" width="18" height="11" rx="1" />
      <motion.path d="M8.5 9 L8.5 5.5 L15.5 5.5 L15.5 9"
        variants={{ rest: { y: 0 }, hover: { y: [0, -1, 0] } }}
        transition={{ duration: 0.2, ease: EASE }} style={SP} />
      <line x1="3" y1="13" x2="21" y2="13" />
      <rect x="10.5" y="11" width="3" height="4" />
    </>
  ),

  // a chat bubble with a play mark — practising a conversation
  simulations: () => (
    <>
      <path d="M4 4 L20 4 L20 16 L11 16 L6 20 L6 16 L4 16 Z" />
      <motion.path d="M10 7.5 L15 10 L10 12.5 Z"
        variants={{ rest: { x: 0 }, hover: { x: [0, 1, 0] } }}
        transition={{ duration: 0.2, ease: EASE }} style={SP} />
    </>
  ),

  'knowledge-base': () => (
    <>
      <path d="M12 5 L5 4 L3 4 L3 18 L5 18 L12 19" />
      <motion.path d="M12 5 L19 4 L21 4 L21 18 L19 18 L12 19"
        variants={{ rest: { rotate: 0 }, hover: { rotate: [0, -2, 0] } }}
        transition={{ duration: 0.2, ease: EASE }} style={HINGE} />
      <path d="M12 5 L12 19" />
    </>
  ),

  goals: () => (
    <>
      <motion.circle cx="12" cy="12" r="9.5"
        variants={{ rest: {}, hover: {}, active: { pathLength: [0, 1] } }}
        transition={{ duration: 0.32, ease: EXPO }} />
      <circle cx="12" cy="12" r="4.5" />
      <motion.circle cx="12" cy="12" r="1" fill="currentColor" stroke="none"
        variants={{ rest: { scale: 1 }, hover: { scale: [1, 1.25, 1] } }}
        transition={{ duration: 0.2, ease: EASE }} style={SP} />
    </>
  ),

  // Scores — a medal: circle + ribbon legs, check inside ("verified grade").
  // Ribbon dips on hover (weight of the medal); active draws the circle on,
  // matching the goals ring signature.
  scores: () => (
    <>
      <motion.circle cx="12" cy="9" r="5.5"
        variants={{ rest: {}, hover: {}, active: { pathLength: [0, 1] } }}
        transition={{ duration: 0.32, ease: EXPO }} />
      <motion.path d="M8.8 13.5 L7 21 L12 18.5 L17 21 L15.2 13.5"
        variants={{ rest: { y: 0 }, hover: { y: [0, 1, 0] } }}
        transition={{ duration: 0.2, ease: EASE }} style={SP} />
      <motion.polyline points="9.8 9 11.4 10.6 14.4 7.4"
        variants={{ rest: { scale: 1 }, hover: { scale: [1, 1.15, 1] } }}
        transition={{ duration: 0.2, ease: EASE }} style={SP} />
    </>
  ),

  // Everything & Anything — four tiles, one deliberately askew ("a bit of
  // everything"). The stray tile snaps square on hover: order out of chaos.
  everything: () => (
    <>
      <rect x="4" y="4" width="7" height="7" />
      <rect x="13" y="4" width="7" height="7" />
      <rect x="4" y="13" width="7" height="7" />
      <motion.rect x="13" y="13" width="7" height="7"
        variants={{ rest: { rotate: 14 }, hover: { rotate: 0 }, active: { rotate: 0 } }}
        transition={{ duration: 0.22, ease: EXPO }} style={SP} />
    </>
  ),

  'vip-host-metrics': () => (
    <>
      <motion.path d="M3 7 L7 13 L12 4 L17 13 L21 7 L21 18 L3 18 Z"
        variants={{ rest: { y: 0 }, hover: { y: [0, -1.5, 0] } }}
        transition={{ duration: 0.2, ease: EASE }} style={SP} />
      <line x1="3" y1="21" x2="21" y2="21" />
    </>
  ),

  // Code brackets </> — "Developer". (A chart here clashed with kyc-stats and
  // read as analytics, not "developer dashboard".) Chevrons spread on hover.
  'developer-dashboard': () => (
    <>
      <motion.polyline points="8.5 8 4.5 12 8.5 16"
        variants={{ rest: { x: 0 }, hover: { x: [0, -1.5, 0] } }}
        transition={{ duration: 0.2, ease: EASE }} style={SP} />
      <motion.polyline points="15.5 8 19.5 12 15.5 16"
        variants={{ rest: { x: 0 }, hover: { x: [0, 1.5, 0] } }}
        transition={{ duration: 0.2, ease: EASE }} style={SP} />
      <line x1="13.5" y1="6" x2="10.5" y2="18" />
    </>
  ),

  // User + settings cog — "manage users". (The shield read as security/verified,
  // not management.) The cog does a quarter-turn on hover.
  'user-management': () => (
    <>
      <circle cx="9" cy="8" r="3.3" />
      <path d="M3.5 20 C3.5 15 14.5 15 14.5 20" />
      <motion.g
        variants={{ rest: { rotate: 0 }, hover: { rotate: 90 } }}
        transition={{ duration: 0.4, ease: EXPO }} style={SP}>
        <polygon points="20.6 7 19.84 8.84 18 9.6 16.16 8.84 15.4 7 16.16 5.16 18 4.4 19.84 5.16" />
        <circle cx="18" cy="7" r="0.8" fill="currentColor" stroke="none" />
      </motion.g>
    </>
  ),

  'qa-manager': () => (
    <>
      <path d="M8 6 L5 6 L5 21 L19 21 L19 6 L16 6" />
      <motion.path d="M9 6 L9 3 L15 3 L15 6 Z"
        variants={{ rest: { y: 0 }, hover: { y: [0, 1, 0] } }}
        transition={{ duration: 0.12, ease: EASE }} style={SP} />
      <motion.polyline points="8.5 13 11 15.5 15.5 10"
        variants={{ rest: { pathLength: 1 }, hover: { pathLength: [0, 1] } }}
        transition={{ duration: 0.2, ease: EXPO, delay: 0.12 }} />
    </>
  ),

  'kyc-goals': () => (
    <>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <circle cx="8.5" cy="10.5" r="2" />
      <path d="M6 15.5 C6 13.5 11 13.5 11 15.5" />
      <motion.line x1="14" y1="9.5" x2="18.5" y2="9.5" strokeWidth="1.5"
        variants={{ rest: { opacity: 0.7 }, hover: { opacity: 1 } }}
        transition={{ duration: 0.15, ease: EASE }} />
      <motion.line x1="14" y1="12.5" x2="18.5" y2="12.5" strokeWidth="1.5"
        variants={{ rest: { opacity: 0.7 }, hover: { opacity: 1 } }}
        transition={{ duration: 0.15, ease: EASE, delay: 0.05 }} />
      <motion.line x1="14" y1="15.5" x2="17" y2="15.5" strokeWidth="1.5"
        variants={{ rest: { opacity: 0.7 }, hover: { opacity: 1 } }}
        transition={{ duration: 0.15, ease: EASE, delay: 0.1 }} />
    </>
  ),

  'payments-goals': () => (
    <>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <line x1="3" y1="9.5" x2="21" y2="9.5" />
      <motion.line x1="6.5" y1="14.5" x2="11.5" y2="14.5" strokeWidth="1.5"
        variants={{ rest: { scaleX: 1 }, hover: { scaleX: [0, 1] } }}
        transition={{ duration: 0.2, ease: EASE }} style={HINGE} />
    </>
  ),

  // Coin — the crypto team's page. The face marks fade in on hover, the way a
  // coin catches light; the ring stays put.
  'crypto-goals': () => (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <motion.path d="M9.5 9h3.25a1.75 1.75 0 0 1 0 3.5H9.5m0 0h3.75a1.75 1.75 0 0 1 0 3.5H9.5M11 7.5V9M11 16v1.5" strokeWidth="1.5"
        variants={{ rest: { opacity: 1 }, hover: { opacity: [0.3, 1] } }}
        transition={{ duration: 0.25, ease: EASE }} />
    </>
  ),

  // Bar chart — uneven bars grow from baseline (distinct from the line chart).
  'kyc-stats': () => (
    <>
      <line x1="3" y1="20" x2="21" y2="20" />
      <motion.rect x="4.5" y="13" width="3.5" height="7"
        variants={{ rest: { scaleY: 1 }, hover: { scaleY: [0.6, 1] } }}
        transition={{ duration: 0.18, ease: EASE }} style={BOTTOM} />
      <motion.rect x="10.25" y="8" width="3.5" height="12"
        variants={{ rest: { scaleY: 1 }, hover: { scaleY: [0.6, 1] } }}
        transition={{ duration: 0.18, ease: EASE, delay: 0.06 }} style={BOTTOM} />
      <motion.rect x="16" y="4" width="3.5" height="16"
        variants={{ rest: { scaleY: 1 }, hover: { scaleY: [0.6, 1] } }}
        transition={{ duration: 0.18, ease: EASE, delay: 0.12 }} style={BOTTOM} />
    </>
  ),

  // Pulse / heartbeat — an ECG spike on a baseline (distinct from the
  // kyc-stats bars and the qa-manager line chart). Redraws on hover.
  'monitoring': () => (
    <>
      <motion.path d="M2 12 H7.5 L9.5 5 L13 19 L15 12 H22"
        variants={{ rest: { pathLength: 1, opacity: 1 }, hover: { pathLength: [0.25, 1] } }}
        transition={{ duration: 0.5, ease: EASE }} />
    </>
  ),

  tl: () => (
    <>
      <circle cx="12" cy="8" r="3.5" />
      <path d="M6 20 C6 15.5 18 15.5 18 20" />
      <motion.circle cx="4.5" cy="9" r="2.5"
        variants={{ rest: { opacity: 0.65, y: 1 }, hover: { opacity: 1, y: 0 } }}
        transition={{ duration: 0.15, ease: EASE }} style={SP} />
      <motion.path d="M2 18 C2 14.5 6 14 7.5 15"
        variants={{ rest: { opacity: 0.65, y: 1 }, hover: { opacity: 1, y: 0 } }}
        transition={{ duration: 0.15, ease: EASE }} style={SP} />
      <motion.circle cx="19.5" cy="9" r="2.5"
        variants={{ rest: { opacity: 0.65, y: 1 }, hover: { opacity: 1, y: 0 } }}
        transition={{ duration: 0.15, ease: EASE, delay: 0.05 }} style={SP} />
      <motion.path d="M22 18 C22 14.5 18 14 16.5 15"
        variants={{ rest: { opacity: 0.65, y: 1 }, hover: { opacity: 1, y: 0 } }}
        transition={{ duration: 0.15, ease: EASE, delay: 0.05 }} style={SP} />
    </>
  ),

  /* ── Sidebar folders (navConfig.js NAV_GROUPS) ─────────────────────────
   * Each folder icon must read as the CATEGORY, not as any one page inside it,
   * or the collapsed rail shows what looks like a duplicate of a child icon. */

  // ACP — the admin control panel: a window frame (title bar) with an account
  // inside, head + shoulders. The head nods on hover. (`user` owns the bare
  // silhouette; the frame is what says "panel".)
  acp: () => (
    <>
      <rect x="3" y="4" width="18" height="16" rx="1" />
      <path d="M3 8 L21 8" />
      <motion.circle cx="12" cy="12.5" r="2"
        variants={{ rest: { y: 0 }, hover: { y: [0, -0.7, 0] } }}
        transition={{ duration: 0.2, ease: EASE }} style={SP} />
      <path d="M8 18 C8 15.2 16 15.2 16 18" />
    </>
  ),

  // Customer Support (folder) — a headset: the arc, two ear pads, a mic boom.
  // The boom draws in on hover. (tools = toolbox, acp = panel; this holds both.)
  cs: () => (
    <>
      <path d="M4 14 L4 12 C4 7.6 7.6 4 12 4 C16.4 4 20 7.6 20 12 L20 14" />
      <rect x="3" y="13" width="4" height="6" rx="1" />
      <rect x="17" y="13" width="4" height="6" rx="1" />
      <motion.path d="M19 19 C19 20.5 17.5 21 16 21 L13 21"
        variants={{ rest: { pathLength: 1 }, hover: { pathLength: [0, 1] } }}
        transition={{ duration: 0.25, ease: EXPO }} />
    </>
  ),

  // Metrics — an axis with a rising trend line and an arrowhead. Distinct from
  // kyc-stats (bars), goals (rings) and vip-host-metrics (crown): this is the
  // abstract "numbers going somewhere". The line redraws on hover.
  metrics: () => (
    <>
      <path d="M3 4 L3 20 L21 20" />
      <motion.polyline points="6 15 10.5 10.5 13.5 13.5 19 7"
        variants={{ rest: { pathLength: 1 }, hover: { pathLength: [0, 1] } }}
        transition={{ duration: 0.3, ease: EXPO }} />
      <motion.polyline points="15 7 19 7 19 11"
        variants={{ rest: { opacity: 1 }, hover: { opacity: [0, 1] } }}
        transition={{ duration: 0.2, ease: EASE, delay: 0.18 }} />
    </>
  ),

  // QA — a shield with a check: "verified quality". (The clipboard is taken by
  // qa-manager, the medal by scores.) Shield breathes, the check redraws.
  qa: () => (
    <>
      <motion.path d="M12 3 L20 6 L20 12 C20 16.5 16.5 19.5 12 21 C7.5 19.5 4 16.5 4 12 L4 6 Z"
        variants={{ rest: { scale: 1 }, hover: { scale: [1, 1.04, 1] } }}
        transition={{ duration: 0.2, ease: EASE }} style={SP} />
      <motion.polyline points="8.5 12 11 14.5 15.5 9.5"
        variants={{ rest: { pathLength: 1 }, hover: { pathLength: [0, 1] } }}
        transition={{ duration: 0.22, ease: EXPO }} />
    </>
  ),

  // Clara AI dashboard — a speech bubble holding three bars: what the assistant
  // says, measured. The bars rise on hover.
  'clara-ai-dashboard': () => (
    <>
      <path d="M4 4 H20 V16 H11 L7 20 V16 H4 Z" />
      <motion.g
        variants={{ rest: { y: 0 }, hover: { y: [0, -1.2, 0] } }}
        transition={{ duration: 0.22, ease: EASE }} style={SP}>
        <line x1="8.5" y1="12.5" x2="8.5" y2="10.5" />
        <line x1="12" y1="12.5" x2="12" y2="8" />
        <line x1="15.5" y1="12.5" x2="15.5" y2="9.5" />
      </motion.g>
    </>
  ),

  // Devs — a terminal: frame, prompt chevron, cursor bar. (developer-dashboard
  // owns the </> brackets.) The prompt nudges and the cursor blinks on hover.
  devs: () => (
    <>
      <rect x="3" y="4" width="18" height="16" rx="1" />
      <motion.polyline points="7 9 10.5 12 7 15"
        variants={{ rest: { x: 0 }, hover: { x: [0, 1, 0] } }}
        transition={{ duration: 0.2, ease: EASE }} style={SP} />
      <motion.line x1="12.5" y1="15" x2="17" y2="15"
        variants={{ rest: { opacity: 1 }, hover: { opacity: [1, 0, 1, 0, 1] } }}
        transition={{ duration: 0.6, ease: 'linear' }} />
    </>
  ),

  logout: () => (
    <>
      <path d="M13 3 L4 3 L4 21 L13 21" />
      <motion.g
        variants={{ rest: { x: 0 }, hover: { x: [0, 2, 0] } }}
        transition={{ duration: 0.2, ease: EASE }} style={SP}>
        <line x1="10" y1="12" x2="21" y2="12" />
        <polyline points="17 8 21 12 17 16" />
      </motion.g>
    </>
  ),

  user: () => (
    <>
      <motion.circle cx="12" cy="8" r="4"
        variants={{ rest: { scale: 1 }, hover: { scale: 1.06 } }}
        transition={{ duration: 0.14, ease: EASE }} style={SP} />
      <path d="M4 21 C4 15 20 15 20 21" />
    </>
  ),
};

export const NIKE_ICON_NAMES = Object.keys(ICONS);

/**
 * NikeNavIcon — presentational. State (hover/active) is propagated from the
 * parent motion.button via framer-motion variants; this component only supplies
 * the variants + geometry. `active` thickens the stroke slightly (color is set
 * by the parent's text-* class).
 */
export default function NikeNavIcon({ name, active = false, size = 20, className = '' }) {
  const render = ICONS[name];
  if (!render) return null;
  return (
    <motion.span
      variants={WRAP}
      className={`inline-flex items-center justify-center ${className}`}
      style={{ transformOrigin: 'center' }}
    >
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={active ? 2.25 : 2}
        strokeLinecap="square"
        strokeLinejoin="miter"
        strokeMiterlimit={4}
        aria-hidden="true"
      >
        {render()}
      </svg>
    </motion.span>
  );
}
